<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" persistent max-width=550 content-class="round-2xl")
  v-card
    v-card-title.px-4
      v-spacer
      .text-xl.font-bold {{ $t('medication_record') }}
      v-spacer
      v-btn(icon color="neutral700" @click="closeDialog")
        v-icon mdi-close
    v-divider
    div.pt-6.px-10
      .text-md.font-semibold {{ $t('past_medication_record') }}
      .text-sm {{ $t('past_medication_record_description') }}
      .d-flex.align-center.my-4
        v-menu(offset-y bottom)
          template(v-slot:activator="{ on }")
            v-btn.text-none(small rounded outlined v-on="on")
              span.text-sm.mr-1 {{ selectedRange.text }}
              v-icon(size="16") $yzi-chevron-down
          v-list.py-0(min-width="200px")
            template(v-for="(range, itemRangeIndex) in itemRange")
              v-divider(v-if="itemRangeIndex !== 0")
              v-list-item(@click="onSelectRange(range)")
                v-list-item-content
                  v-list-item-title.text-sm {{ range.text }}
        v-spacer
        v-btn(icon color="neutral500" @click="onClickSearch")
          v-icon(size="20") $yzi-search-sm
        v-text-field(v-if="showSearchField" v-model="searchText" @blur="onBlurSearch" @input="queryChanged" solo dense flat placeholder="Type to search..." hide-details clearable autofocus)
        v-menu(offset-y bottom)
          template(v-slot:activator="{ on }")
            v-btn(v-if="selectedSort.text === $t('clear_sort')" icon color="neutral500" v-on="on")
              v-icon(size="20") $yzi-filter-line
            v-btn.selected-button(v-else text small color="neutral" v-on="on")
              v-icon(size="20") $yzi-menu-03
              v-icon(size="13") {{ selectedSort.text === $t('newest_to_oldest') ? '$yzi-arrow-up' : '$yzi-arrow-down' }}
          v-list.py-0(min-width="200px")
            .text-md.font-semibold.px-4.py-2 {{ $t('sort') }}
            template(v-for="(sort, sortItemIndex) in sortItems")
              v-divider(v-if="sortItemIndex !== 0")
              v-list-item(@click="onSelectSort(sort)")
                v-list-item-content
                  v-list-item-title.text-sm(:class="sort.text === $t('clear_sort') ? 'danger500--text' : ''") {{ sort.text }}
      template(v-if="isFetching")
        v-skeleton-loader.px-4(type="list-item-three-line@4")
      template(v-else-if="totalCount === 0")
        .d-flex.align-center.justify-center.fill-height.pa-10
          .text-sm.neutral400--text {{ $t('item_not_found', { item: $t('patient_history').toLowerCase() }) }}
      template(v-else)
        .middle-content(:key="medicalRecordItemKey")
          template(v-for="(history, historyIndex) in patientHistories")
            medical-record-item(:parentHistory="history" :selectedMedication="selectedMedication" :index="historyIndex" @updated="onUpdateSelectedMedication")
        v-divider
        div.d-flex.align-center.py-2
          v-spacer
          div
            yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" text="Item list" @input="pageChanged")
    v-divider
    v-card-actions.px-6
      v-btn.button-md.neutral700--text.px-0.underline(text @click="closeDialog") {{ $t('back') }}
      v-spacer
      v-btn.button-md(:loading="isLoading" color="primary" depressed @click="onAddToDispensary" :disabled="!canProceed") {{ $t('add_to_dispensary') }}
</template>

<script>
import { debounce } from '@/utils'
import { mapGetters, mapActions } from 'vuex'
import MedicalRecordItem from '@/components/clinic/invoice/MedicalRecordItem.vue'

export default {
  components: {
    MedicalRecordItem,
  },
  props: {
    open: {
      type: Boolean,
    },
    parentPatient: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isFetching: false,
      isLoading: false,
      medicalRecordItemKey: 0,
      totalCount: 0,
      pageSize: 20,
      currentPage: 1,
      selectedRange: { text: this.$t('all_time'), value: null },
      selectedSort: { text:this.$t('clear_sort'), value:null },
      showSearchField: false,
      searchText: null,
      itemRangeOptions: [
        { text: this.$t('all_time'), value: null },
        { text: this.$t('past_1_month'), value: 1 },
        { text: this.$t('past_3_months'), value: 3 },
        { text: this.$t('past_6_months'), value: 6 },
        { text: this.$t('past_12_months'), value: 12 },
      ],
      sortOptions: [
        { text:this.$t('newest_to_oldest'), value:'-created' },
        { text:this.$t('oldest_to_newest'), value:'created' },
        { text:this.$t('clear_sort'), value:null },
      ],
      patientHistories: null,
      selectedMedication: []
    }
  },
  watch: {
    currentPage() {
      this.fetchPatientHistory()
    },
    pageSize() {
      this.fetchPatientHistory()
    },
    selectedRange() {
      this.fetchPatientHistory()
    },
    selectedSort() {
      this.fetchPatientHistory()
    },
  },
  mounted () {
    this.fetchPatientHistory()
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      hqSelectedCompany: 'hq/getSelectedCompany',
      isHq: 'hq/getIsHq'
    }),
    showDialog: {
      get() {
        return this.open
      },
      set(newValue) {
        this.$emit('update:open', newValue)
      }
    },
    leadPid() {
      return this.$route.params.pid
    },
    itemRange() {
      return this.itemRangeOptions.filter(item => this.selectedRange.text !== item.text)
    },
    sortItems() {
      return this.sortOptions.filter(item => this.selectedSort.text !== item.text)
    },
    params() {
      return {
        search: this.searchText,
        past_months: this.selectedRange.value,
        page_size: this.pageSize,
        page: this.currentPage,
        ordering: this.selectedSort.value
      }
    },
    payload() {
      const payload = this.selectedMedication.filter(item => item?.data.length > 0).map(item => item.data.map(eachData => this.filterPayloadObject(eachData)))
      if (payload.length > 0) {
        return payload.flat()
      }
      return []
    },
    canProceed() {
      return this.payload.length > 0
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    closeDialog() {
      this.$emit('cancel')
      this.showDialog = false
    },
    pageChanged(page) {
      this.currentPage = page
    },
    onSelectRange(selectedRange) {
      this.selectedRange = selectedRange
    },
    onSelectSort(selectedSort) {
      this.selectedSort = selectedSort
    },
    onClickSearch() {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false
        }
      } else {
        this.showSearchField = true
      }
    },
    onBlurSearch() {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false
      }
    },
    queryChanged: debounce(function () {
      this.fetchPatientHistory()
    }, 750),
    onUpdateSelectedMedication({ data, pid }) {
      let found = this.selectedMedication.find(item => item.pid === pid)
      if (found) {
        found.data = [...data]
      } else {
        this.selectedMedication.push({ pid, data: [...data] })
      }
    },
    async fetchPatientHistory() {
      try {
        this.isFetching = true
        const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
        const { data } = await this.$axios.get(`/clinics/${companySlug}/patients/${this.parentPatient.id}/histories`, { params: this.params })
        this.patientHistories = data.results
        this.totalCount = data.count
        this.isFetching = false
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.patientHistories = []
          this.totalCount = 0
          this.isFetching = false
          return
        }
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      }
    },
    filterPayloadObject(obj) {
      const allowedProperties = ['product', 'quantity', 'price', 'extra_data']
      const filteredObj = this.$options.filters.filterAllowedObjectProperties(obj, allowedProperties)
      filteredObj.product = filteredObj.product.id
      return filteredObj
    },
    async onAddToDispensary() {
      this.isLoading = true
      try {
        await this.$axios.patch(`/leads/${this.leadPid}`, { products: this.payload })
        this.$emit('updated')
        this.selectedMedication = []
        this.medicalRecordItemKey++
        this.showMessage(['success', this.$t('success_message.medication_added_successfully')])
      } catch (err) {
        let message = this.$t('error_message.unable_to_complete_request')
        if (err.response?.status === 400 && err.response?.data?.code === '444101') {
          message = 'Item quantity more than available stock. Please adjust the quantity'
        }
        if (err.response.data) {
          if (err.response.data.detail) {
            message = err.response.data.detail
          } else {
            let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
            if (data) { message = data }
          }
        }
        this.showMessage(['error', message])
      } finally {
        this.isLoading = false
      }
    }
  },
}
</script>

<style scoped>
.middle-content {
  max-height: 40vh;
  overflow-y: auto !important;
  flex-grow: 1;
}
</style>